/**
 * Owner: Masa Bayu
 * File: segment-spacing.ts
 * Date:Time: 2026-10-15 08:20
 * Description: Per-segment spacing · kotak QXW ialah target dakwat · trim-end · whitespace-only → 0.
 */

import type { Font, LinePaintSegment, LinePaintSegmentOut } from './types.js';

/** Belanja baki (kotak QXW − semula jadi) ke atas satu pemisah glif/ruang. */
export type SegmentSpacing = {
  readonly wordSpacingPt: number;
  readonly letterSpacingPt: number;
};

const ZERO: SegmentSpacing = Object.freeze({ wordSpacingPt: 0, letterSpacingPt: 0 });

/**
 * Kontrak QXW: `width` ialah advance rekod bagi `text` itu, jadi hujung kanan
 * dakwat mesti jatuh pada `x + width`. Dua belanjawan sahaja: ruang dalaman
 * (`word-spacing`) atau jarak glif (`letter-spacing`). Whitespace-only segment
 * ialah kotak ruang PDF · tiada belanja · trailing inject space tidak diukur
 * (ia duduk sebagai segmen CAT1 tersendiri, bukan ruang palsu).
 *
 * §488-A ukur 3 baris `IN_WORD` berlubang 0.51–8.7 pt walaupun kotaknya sudah
 * bersentuhan: hukum lama hanya tahu membelanjakan baki pada ruang, jadi segmen
 * tanpa ruang (`pemerinta` + `hannya?`) meninggalkan lubang yang sama.
 */
export function segmentSpacingFor(
  segment: LinePaintSegment,
  font: Font | undefined,
  sizePt: number,
): SegmentSpacing {
  if (/^\s+$/u.test(segment.text)) return ZERO;
  if (!font) return ZERO;
  const forMeasure = segment.text.replace(/\s+$/u, '');
  if (forMeasure.length === 0) return ZERO;
  let spaces = 0;
  for (let i = 0; i < forMeasure.length; i += 1) {
    if (forMeasure[i] === ' ') spaces += 1;
  }
  const natural = font.measureWidth(forMeasure, sizePt);
  const slack = segment.width - natural;
  if (spaces > 0) return { wordSpacingPt: slack / spaces, letterSpacingPt: 0 };
  const gaps = forMeasure.length - 1;
  if (gaps < 1) return ZERO;
  return { wordSpacingPt: 0, letterSpacingPt: slack / gaps };
}

export function paintSegmentsOut(
  segments: readonly LinePaintSegment[],
  font: Font | undefined,
  sizePt: number,
): readonly LinePaintSegmentOut[] {
  return segments.map((seg) => {
    const spacing = segmentSpacingFor(seg, font, sizePt);
    return Object.freeze({
      text: seg.text,
      x: seg.x,
      width: seg.width,
      wordSpacingPt: spacing.wordSpacingPt,
      letterSpacingPt: spacing.letterSpacingPt,
    });
  });
}
